<script setup>
import { ref, onMounted, defineProps, defineEmits } from 'vue';
import axios from 'axios';
import CustomTable from '@/components/table/CustomTable.vue';

const props = defineProps({
    evalDatasetId: {
        type: [String, Number],
        required: true,
    },
    cachedData: {
        type: Array,
        default: () => [],
    },
    hasCachedData: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['leaderboard-data']);

const columns = [
    {
        label: '',
        key: 'rank',
        cellClass: 'font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'number',
    },
    {
        label: 'Agent Harness',
        key: 'agent_harness',
        cellClass: 'font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'text',
    },
    {
        label: 'Model Name',
        key: 'model_name',
        cellClass: 'font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'text',
    },
    {
        label: 'Score',
        key: 'score',
        cellClass: 'font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'number',
    },
    {
        label: 'Run Time',
        key: 'run_time',
        cellClass: 'pr-12 text-right font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'number',
        formatter: (value) => formatRunTime(value),
    },
    {
        label: 'Relative Cost',
        key: 'relative_cost',
        cellClass: 'pr-12 text-center font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'text',
    },
    {
        label: 'Issue Date',
        key: 'issue_date',
        cellClass: 'font-normal text-white whitespace-nowrap',
        sortable: true,
        type: 'date',
    }
];

const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const sanitizeIconUrl = (value) => {
    if (typeof value !== 'string') return null;

    const trimmed = value.trim();
    if (!trimmed) return null;

    if (trimmed.startsWith('/')) return trimmed;

    try {
        const url = new URL(trimmed);
        return url.protocol === 'https:' ? url.href : null;
    } catch {
        return null;
    }
};

function formatRunTime(ms) {
  if (ms === null) return "-";

  if (ms === 'NA') {
    ms = 0;
  }

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];

  if (totalSeconds < 1) {
    const decimalSeconds = ms / 1000;
    return `${decimalSeconds.toFixed(2)}s`;
  }

  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(" ");
}

const interpolateColor = (color1, color2, factor) => {
    const result = color1.map((channel, i) => 
        Math.round(channel + factor * (color2[i] - channel))
    );
    return result;
};

const getScoreGradient = (value) => {
    const percentage = value * 100;
    
    const red = [255, 20, 20];
    const yellow = [255, 220, 0];
    const green = [0, 255, 100];
    
    let startColor, midColor, endColor;
    
    if (percentage <= 15) {
        const darkerRed = [200, 15, 15];
        const brighterRed = [255, 40, 40];
        startColor = darkerRed;
        midColor = red;
        endColor = brighterRed;
    } else if (percentage <= 50) {
        const factor = (percentage - 15) / 35;
        startColor = interpolateColor(red, yellow, factor);
        midColor = interpolateColor(red, yellow, Math.min(1, factor + 0.15));
        endColor = interpolateColor(red, yellow, Math.min(1, factor + 0.3));
    } else {
        const factor = (percentage - 50) / 50;
        startColor = interpolateColor(yellow, green, factor);
        midColor = interpolateColor(yellow, green, Math.min(1, factor + 0.15));
        endColor = interpolateColor(yellow, green, Math.min(1, factor + 0.3));
    }
    
    return `linear-gradient(to right, 
        rgb(${startColor.join(', ')}), 
        rgb(${midColor.join(', ')}) 50%, 
        rgb(${endColor.join(', ')})
    )`;
};

const data = ref([]);
const isLoading = ref(false);

const getEvalLeaderboard = async () => {
    try {
        isLoading.value = true;
        const response = await axios.get(`/api/eval-runs/leaderboard/${props.evalDatasetId}`);

        if (response.data && response.data.leaderboards && Array.isArray(response.data.leaderboards)) {
            const sortedLeaderboards = [...response.data.leaderboards].sort((a, b) => b.score - a.score);

            data.value = sortedLeaderboards.map((item, index) => {
                const latency = Number(item?.metrics?.latency);
                return {
                    rank: index + 1,
                    llm_icon: item.llm_icon ?? '',
                    model_name: item.model_name,
                    agent_harness: item.agent_harness ?? 'N/A',
                    icon: sanitizeIconUrl(item.icon),
                    issue_date: formatDate(new Date(item.issue_date)),
                    score: item.score,
                    run_time: isNaN(latency) || latency < 0 ? 0 : latency,
                    relative_cost: item?.metrics?.cost ?? '',
                };
            });
            emit('leaderboard-data', data.value);
        } else {
            data.value = [];
            emit('leaderboard-data', []);
        }
    } catch (error) {
        console.error(error);
    } finally {
        isLoading.value = false;
    }
};

onMounted(() => {
    if (props.hasCachedData) {
        data.value = Array.isArray(props.cachedData) ? props.cachedData : [];
        return;
    }
    getEvalLeaderboard();
});


</script>

<template>
    <div class="flex flex-col gap-4">
    <div v-if="isLoading" class="h-[300px] flex justify-center items-center">
        <div class="flex justify-center items-center h-full">
            <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-echoes-primary"></div>
        </div>
    </div>
    <div v-else>
        <CustomTable 
            :data="data"
            :columns="columns"
            empty-state-icon="mdi mdi-trophy"
            empty-state-message="No leaderboard data available"
            search-placeholder="Search..."
            :searchable="true"
        >
            <template #cell-model_name="{ value, item }">
                <div class="flex items-center gap-3">
                    <div 
                        v-if="item.icon" 
                        class="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden bg-echoes-background-black border border-echoes-border-light flex-shrink-0"
                    >
                        <img 
                            :src="item.icon" 
                            :alt="item.model_name || 'LLM Icon'" 
                            class="w-full h-full object-cover"
                        />
                    </div>
                    <div 
                        v-else 
                        class="w-8 h-8 rounded-full flex items-center justify-center bg-echoes-background-black border border-echoes-border-light flex-shrink-0"
                    >
                        <i class="mdi mdi-robot text-white/40 text-sm"></i>
                    </div>
                    <span class="text-sm text-white truncate" :title="value">
                        {{ value }}
                    </span>
                </div>
            </template>

            <template #cell-agent_harness="{ value }">
                <span class="text-sm text-white/80 truncate" :title="value">
                    {{ value }}
                </span>
            </template>
            
            <template #cell-score="{ value }">
                <div class="flex items-center gap-3">
                <div class="flex w-32 h-5 bg-echoes-background-gray rounded-full overflow-hidden">
                    <div 
                        class="h-full transition-all duration-300 rounded-full"
                        :style="{ 
                            width: `${(value * 100)}%`,
                            background: getScoreGradient(value)
                        }"
                    ></div>
                </div>
                <span 
                    class="text-white font-semibold text-sm whitespace-nowrap transition-all duration-300"
                >
                    {{ Math.round(value * 100) }}%
                </span>
            </div>
                </template>
            </CustomTable>
        </div>
    </div>
</template>
